import { PdfFileValidationError } from "@/domain";

export const PDF_MAX_BYTES = 250 * 1024 * 1024;
export const PDF_HEADER_SCAN_BYTES = 1024;

const PDF_HEADER_BYTES = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d]);

export type PdfValidationOptions = {
  maximumByteLength?: number;
  headerScanBytes?: number;
};

export type ValidatedPdfHeader = {
  byteLength: number;
  contentType: "application/pdf";
  headerOffset: number;
  pdfVersion: string | null;
};

function findSubarray(haystack: Uint8Array, needle: Uint8Array): number {
  if (needle.length === 0) {
    return 0;
  }

  const lastStart = haystack.length - needle.length;
  for (let start = 0; start <= lastStart; start += 1) {
    let matches = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[start + offset] !== needle[offset]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return start;
    }
  }

  return -1;
}

function readPdfVersion(bytes: Uint8Array, headerOffset: number): string | null {
  const major = bytes[headerOffset + PDF_HEADER_BYTES.length];
  const dot = bytes[headerOffset + PDF_HEADER_BYTES.length + 1];
  const minor = bytes[headerOffset + PDF_HEADER_BYTES.length + 2];

  if (
    major === undefined ||
    dot !== 0x2e ||
    minor === undefined ||
    major < 0x30 ||
    major > 0x39 ||
    minor < 0x30 ||
    minor > 0x39
  ) {
    return null;
  }

  return `${String.fromCharCode(major)}.${String.fromCharCode(minor)}`;
}

export function validatePdfHeaderBytes(
  headerBytes: Uint8Array,
  byteLength: number,
  options: PdfValidationOptions = {},
): ValidatedPdfHeader {
  const maximumByteLength =
    options.maximumByteLength ?? PDF_MAX_BYTES;
  const headerScanBytes = options.headerScanBytes ?? PDF_HEADER_SCAN_BYTES;

  if (!Number.isSafeInteger(byteLength) || byteLength < 0) {
    throw new TypeError("PDF byteLength must be a non-negative safe integer.");
  }

  if (
    !Number.isSafeInteger(maximumByteLength) ||
    maximumByteLength <= 0
  ) {
    throw new RangeError(
      "The configured PDF size limit must be a positive safe integer.",
    );
  }

  if (!Number.isSafeInteger(headerScanBytes) || headerScanBytes < 5) {
    throw new RangeError(
      "The configured PDF header scan region must be at least five bytes.",
    );
  }

  if (byteLength === 0) {
    throw new PdfFileValidationError(
      "invalid-pdf",
      "The selected file is empty.",
      { byteLength },
    );
  }

  if (byteLength > maximumByteLength) {
    throw new PdfFileValidationError(
      "pdf-too-large",
      `This PDF is larger than the ${Math.floor(
        maximumByteLength / (1024 * 1024),
      )} MiB browser-memory limit.`,
      { byteLength, maximumByteLength },
    );
  }

  const permittedHeader = headerBytes.subarray(
    0,
    Math.min(headerScanBytes, byteLength, headerBytes.length),
  );
  const headerOffset = findSubarray(permittedHeader, PDF_HEADER_BYTES);

  if (headerOffset < 0) {
    throw new PdfFileValidationError(
      "invalid-pdf",
      "The selected file does not contain a valid PDF header.",
      { byteLength },
    );
  }

  return {
    byteLength,
    contentType: "application/pdf",
    headerOffset,
    pdfVersion: readPdfVersion(permittedHeader, headerOffset),
  };
}

export async function validatePdfBlob(
  blob: Blob,
  options: PdfValidationOptions = {},
): Promise<ValidatedPdfHeader> {
  const headerScanBytes = options.headerScanBytes ?? PDF_HEADER_SCAN_BYTES;

  if (blob.size === 0 || blob.size > (options.maximumByteLength ?? PDF_MAX_BYTES)) {
    return validatePdfHeaderBytes(new Uint8Array(), blob.size, options);
  }

  let headerBuffer: ArrayBuffer;
  try {
    headerBuffer = await blob.slice(0, headerScanBytes).arrayBuffer();
  } catch (error) {
    throw new PdfFileValidationError(
      "invalid-pdf",
      "The browser could not read the selected file.",
      { byteLength: blob.size, cause: error },
    );
  }

  return validatePdfHeaderBytes(
    new Uint8Array(headerBuffer),
    blob.size,
    options,
  );
}
